/**
 * Zuordnung von Kontoauszugs-Umsaetzen zu offenen Rechnungen (M8).
 *
 * Die Funktion schlaegt vor und bucht nie. Das ist keine Vorsicht um der
 * Vorsicht willen: eine falsch zugeordnete Zahlung setzt eine Rechnung auf
 * bezahlt, die es nicht ist, und laesst zugleich eine andere offen stehen und
 * in die Mahnung laufen. Beide Fehler faellt nur auf, wer den Kontoauszug
 * ohnehin liest — also genau in dem Moment, in dem der Vorschlag bestaetigt
 * wird.
 *
 * Die Regeln greifen in absteigender Sicherheit:
 *   1. Rechnungsnummer im Verwendungszweck            -> high
 *      (auch nur ihre Ziffern, ohne Kuerzel "RE-")
 *   2. fast dieselbe Nummer (ein Tippfehler) UND
 *      genau der offene Betrag                        -> medium
 *   3. Betrag entspricht genau einem offenen Posten   -> medium
 *   4. Kundenname loest gleiche Betraege auf          -> low
 *   5. sonst kein Vorschlag
 */

import { Customer, Invoice } from '../../types';
import { openAmountCents } from '../invoiceLogic';
import { checkCashDiscount, matchesAgreedCashDiscount } from '../cashDiscount';
import { Cents, formatCents } from '../money';
import { BankTransaction } from './camt053';

export type MatchConfidence = 'high' | 'medium' | 'low';

export type MatchRule =
    /** Rechnungsnummer stand im Verwendungszweck. */
    | 'invoice_number'
    /** Fast dieselbe Nummer (ein Tippfehler) und genau der offene Betrag. */
    | 'similar_number'
    /** Betrag entspricht genau dem offenen Betrag einer einzigen Rechnung. */
    | 'open_amount'
    /** Gleicher Betrag bei mehreren Rechnungen, aufgeloest ueber den Zahlernamen. */
    | 'customer_amount'
    /** Mehrere gleich gute Treffer — bewusst kein Vorschlag. */
    | 'ambiguous'
    /** Die Rechnung wurde bereits einem anderen Umsatz zugeordnet. */
    | 'duplicate_invoice'
    /** Zahlungsausgang, kein Eingang. */
    | 'outgoing'
    /** Nur vorgemerkt, noch nicht gebucht. */
    | 'pending'
    /** bank_tx_id ist bereits als Zahlung erfasst. */
    | 'already_imported'
    /** Nichts gefunden. */
    | 'unmatched';

export interface PaymentMatch {
    transaction: BankTransaction;
    /** Der Vorschlag. Null heisst: es gibt keinen, der belastbar genug waere. */
    invoice: Invoice | null;
    /**
     * Gleich gut passende Rechnungen. Gefuellt, wenn die Zuordnung mehrdeutig
     * ist — die Oberflaeche kann daraus eine Auswahl anbieten, statt dass hier
     * willkuerlich die erste genommen wird.
     */
    candidates: Invoice[];
    confidence: MatchConfidence;
    /** Begruendung in deutschem Klartext, unveraendert anzeigbar. */
    reason: string;
    rule: MatchRule;
}

export interface MatchOptions {
    /**
     * Kunden zu den Rechnungen. Nur noetig, wenn Invoice.customerName nicht
     * gesetzt ist (das Feld stammt aus einem Join und fehlt je nach Abfrage).
     */
    customers?: readonly Customer[];
    /**
     * Zulaessige Abweichung beim Betragsvergleich in Cent. Standard 0, also
     * exakt — Skonto oder Bankgebuehren sind eine bewusste Entscheidung des
     * Anwenders und keine Toleranz, die das System stillschweigend annimmt.
     */
    toleranceCents?: Cents;
    /** Bereits gebuchte Umsaetze (bank_tx_id) — werden nicht erneut vorgeschlagen. */
    alreadyImportedBankTxIds?: readonly string[];
}

// ═══════════════════════════════════════════════════════════════════════════
// Textvergleiche
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Vergleichsform einer Rechnungsnummer: nur Buchstaben und Ziffern.
 *
 * Damit trifft "RE-2026-0042" auch dann, wenn der Zahler "RE 2026 0042" oder
 * "RE2026/0042" in den Verwendungszweck geschrieben hat — Banken und
 * Online-Formulare zerlegen und normalisieren den Text unterschiedlich.
 */
const normalizeKey = (value: string): string => value.toUpperCase().replace(/[^A-Z0-9]/g, '');

/**
 * Kurze Nummern werden nicht gesucht: eine dreistellige Nummer steckt zufaellig
 * in jeder IBAN, jedem Datum und jeder Mandatsreferenz.
 */
const MIN_NUMBER_KEY_LENGTH = 5;

/**
 * Ziffernkern einer Rechnungsnummer: die Vergleichsform ohne fuehrendes
 * Kuerzel ("RE-26091043" -> "26091043"). Manche Zahler schreiben nur die
 * Ziffern. Gesucht wird der Kern nur als ganze Ziffernfolge und erst ab sechs
 * Stellen — kuerzere Folgen stecken zufaellig in Datum, Betrag und
 * Kundennummer.
 */
const MIN_DIGIT_CORE_LENGTH = 6;

const digitCoreOf = (invoiceNumber: string): string => {
    const core = normalizeKey(invoiceNumber).replace(/^[A-Z]+/, '');
    return /^\d+$/.test(core) && core.length >= MIN_DIGIT_CORE_LENGTH ? core : '';
};

/**
 * Ganze Ziffernfolgen im Text ab Kernlaenge — "RE-266091039/12.09." ergibt
 * nur 266091039; Tag und Monat fallen als zu kurz heraus.
 */
const digitTokensOf = (text: string): string[] => {
    const tokens: string[] = text.match(/\d+/g) ?? [];
    return tokens.filter((token) => token.length >= MIN_DIGIT_CORE_LENGTH);
};

/**
 * Unterscheiden sich zwei Nummern um genau einen Tippfehler?
 *
 * Ein Zeichen zu viel, eines zu wenig, eines falsch oder zwei benachbarte
 * vertauscht. Gleiche Nummern zaehlen nicht — die hat Regel 1 schon gefunden.
 */
const withinOneEdit = (a: string, b: string): boolean => {
    if (a === b || Math.abs(a.length - b.length) > 1) return false;
    if (a.length === b.length) {
        const diffs: number[] = [];
        for (let i = 0; i < a.length && diffs.length <= 2; i++) {
            if (a[i] !== b[i]) diffs.push(i);
        }
        if (diffs.length === 1) return true;
        return (
            diffs.length === 2 &&
            diffs[1] === diffs[0] + 1 &&
            a[diffs[0]] === b[diffs[1]] &&
            a[diffs[1]] === b[diffs[0]]
        );
    }
    const [shorter, longer] = a.length < b.length ? [a, b] : [b, a];
    let i = 0;
    while (i < shorter.length && shorter[i] === longer[i]) i++;
    return shorter.slice(i) === longer.slice(i + 1);
};

const foldGerman = (value: string): string =>
    value
        .toLowerCase()
        .replace(/ä/g, 'ae')
        .replace(/ö/g, 'oe')
        .replace(/ü/g, 'ue')
        .replace(/ß/g, 'ss');

/** Rechtsformen und Anreden tragen zur Unterscheidung nichts bei. */
const NOISE_TOKENS = new Set([
    'gmbh',
    'mbh',
    'ohg',
    'gbr',
    'kgaa',
    'und',
    'firma',
    'herr',
    'frau',
    'gesellschaft',
    'bau',
]);

const normalizeName = (value: string): string =>
    foldGerman(value)
        .replace(/[^a-z0-9]+/g, ' ')
        .trim();

const significantTokens = (value: string): string[] =>
    normalizeName(value)
        .split(' ')
        .filter((token) => token.length >= 3 && !NOISE_TOKENS.has(token));

/**
 * Namensvergleich ueber Wortbestand statt ueber Gleichheit.
 *
 * Der Kontoauszug liefert den Namen so, wie ihn die Bank des Zahlers fuehrt:
 * abgekuerzt, in Grossbuchstaben, mit oder ohne Rechtsform, gelegentlich auf
 * 27 Zeichen beschnitten. Verlangt wird deshalb nur, dass alle
 * aussagekraeftigen Woerter des kuerzeren Namens im laengeren vorkommen.
 */
const namesMatch = (a: string, b: string): boolean => {
    const tokensA = significantTokens(a);
    const tokensB = significantTokens(b);
    if (!tokensA.length || !tokensB.length) return false;
    const [shorter, longer] = tokensA.length <= tokensB.length ? [tokensA, tokensB] : [tokensB, tokensA];
    return shorter.every((token) => longer.includes(token));
};

/**
 * Kundenname im Verwendungszweck.
 *
 * Verlangt mindestens ein Wort mit fuenf Zeichen, sonst wuerde ein Kunde
 * namens "Alt AG" in fast jedem Text auftauchen.
 */
const nameAppearsInText = (name: string, text: string): boolean => {
    const tokens = significantTokens(name);
    if (!tokens.some((token) => token.length >= 5)) return false;
    const haystack = normalizeName(text);
    return tokens.every((token) => haystack.includes(token));
};

// ═══════════════════════════════════════════════════════════════════════════
// Zuordnung
// ═══════════════════════════════════════════════════════════════════════════

const CONFIDENCE_RANK: Record<MatchConfidence, number> = { high: 3, medium: 2, low: 1 };

const invoiceLabel = (invoice: Invoice): string =>
    invoice.invoiceNumber ? `Rechnung ${invoice.invoiceNumber}` : 'einer Rechnung ohne Nummer';

const amountsEqual = (a: Cents, b: Cents, toleranceCents: Cents): boolean =>
    Math.abs(a - b) <= toleranceCents;

export const matchPayments = (
    transactions: readonly BankTransaction[],
    invoices: readonly Invoice[],
    options: MatchOptions = {},
): PaymentMatch[] => {
    const tolerance = Math.max(0, options.toleranceCents ?? 0);
    const imported = new Set(options.alreadyImportedBankTxIds ?? []);
    const customersById = new Map((options.customers ?? []).map((customer) => [customer.id, customer]));

    const customerNameOf = (invoice: Invoice): string =>
        invoice.customerName || customersById.get(invoice.customerId)?.name || '';

    // Der offene Betrag wird gerechnet und nicht aus Invoice.openCents gelesen:
    // die Spalte kommt aus der Datenbank und ist in einem Datensatz, der seit
    // dem Laden im Speicher liegt, moeglicherweise nicht mehr aktuell.
    const openOf = (invoice: Invoice): Cents => openAmountCents(invoice);
    const openInvoices = invoices.filter((invoice) => openOf(invoice) > 0);

    const matchOne = (transaction: BankTransaction): PaymentMatch => {
        const empty = { transaction, invoice: null, candidates: [] as Invoice[] };

        if (imported.has(transaction.bankTxId)) {
            return {
                ...empty,
                confidence: 'low',
                rule: 'already_imported',
                reason: 'Dieser Umsatz wurde bereits als Zahlung erfasst.',
            };
        }
        if (!transaction.booked) {
            return {
                ...empty,
                confidence: 'low',
                rule: 'pending',
                reason: 'Der Umsatz ist nur vorgemerkt und noch nicht gebucht. Er kann sich noch ändern und wird deshalb nicht zugeordnet.',
            };
        }
        if (transaction.amountCents === 0) {
            return {
                ...empty,
                confidence: 'low',
                rule: 'unmatched',
                reason: 'Der Umsatz hat keinen Betrag.',
            };
        }
        if (transaction.amountCents < 0) {
            return {
                ...empty,
                confidence: 'low',
                rule: 'outgoing',
                reason: `Zahlungsausgang über ${formatCents(
                    Math.abs(transaction.amountCents),
                )} (Lastschrift, Erstattung oder Gebühr). Ein Ausgang wird nicht als Zahlungseingang vorgeschlagen.`,
            };
        }

        // Die Ende-zu-Ende-Referenz wird mitdurchsucht: Zahlungen aus einem
        // Buchhaltungsprogramm tragen die Rechnungsnummer oft dort und nicht
        // im Verwendungszweck.
        const searchText = `${transaction.remittanceInfo} ${transaction.endToEndId ?? ''}`;
        const searchKey = normalizeKey(searchText);
        const digitTokens = digitTokensOf(searchText);
        const byFullNumber = invoices.filter((invoice) => {
            const key = normalizeKey(invoice.invoiceNumber ?? '');
            return key.length >= MIN_NUMBER_KEY_LENGTH && searchKey.includes(key);
        });
        // Die Ziffern allein zaehlen erst, wenn keine vollstaendige Nummer
        // gefunden wurde. Sonst traefe "RE-26091043" bei getrennten
        // Nummernkreisen auch "AR-26091043", und die Zuordnung waere mehrdeutig.
        const numbered = byFullNumber.length
            ? byFullNumber
            : invoices.filter((invoice) => {
                  const core = digitCoreOf(invoice.invoiceNumber ?? '');
                  return !!core && digitTokens.includes(core);
              });
        const mentioned = (invoice: Invoice): string =>
            byFullNumber.length
                ? `${invoiceLabel(invoice)} steht im Verwendungszweck`
                : `${invoiceLabel(invoice)} steht ohne Kürzel im Verwendungszweck`;

        if (numbered.length > 1) {
            return {
                transaction,
                invoice: null,
                candidates: numbered,
                confidence: 'low',
                rule: 'ambiguous',
                reason: `Der Verwendungszweck nennt mehrere Rechnungsnummern (${numbered
                    .map((invoice) => invoice.invoiceNumber)
                    .join(', ')}). Bitte von Hand aufteilen.`,
            };
        }

        if (numbered.length === 1) {
            const invoice = numbered[0];
            const open = openOf(invoice);
            if (open <= 0) {
                return {
                    transaction,
                    invoice,
                    candidates: [],
                    confidence: 'medium',
                    rule: 'invoice_number',
                    reason: `${mentioned(
                        invoice,
                    )}, ist aber bereits ausgeglichen. Bitte auf Doppelzahlung prüfen.`,
                };
            }
            if (amountsEqual(transaction.amountCents, open, tolerance)) {
                return {
                    transaction,
                    invoice,
                    candidates: [],
                    confidence: 'high',
                    rule: 'invoice_number',
                    reason: `${mentioned(
                        invoice,
                    )} und der Betrag entspricht dem offenen Betrag.`,
                };
            }
            // Liegt der Betrag genau um das vereinbarte Skonto darunter und kam
            // die Zahlung in der Frist, ist es keine Teilzahlung. Gebucht wird
            // das Skonto trotzdem nur auf ausdruecklichen Haken im Dialog.
            const shortfall = open - transaction.amountCents;
            const skontoFits =
                shortfall > 0 &&
                matchesAgreedCashDiscount(invoice, shortfall, transaction.valueDate) &&
                checkCashDiscount(invoice, shortfall, transaction.valueDate, transaction.amountCents)
                    .length === 0;
            return {
                transaction,
                invoice,
                candidates: [],
                confidence: 'medium',
                rule: 'invoice_number',
                reason: skontoFits
                    ? `${mentioned(invoice)}; der Betrag liegt um ${formatCents(
                          shortfall,
                      )} unter dem offenen Betrag — das entspricht dem vereinbarten Skonto.`
                    : `${mentioned(invoice)}, der Betrag weicht vom offenen Betrag (${formatCents(
                          open,
                      )}) ab — vermutlich eine Teilzahlung.`,
            };
        }

        const payer = transaction.counterpartyName ?? '';
        const matchesPayer = (invoice: Invoice): boolean => {
            const customerName = customerNameOf(invoice);
            if (!customerName) return false;
            return (
                (!!payer && namesMatch(customerName, payer)) ||
                nameAppearsInText(customerName, transaction.remittanceInfo)
            );
        };

        // Fast dieselbe Nummer — ein Zeichen zu viel, zu wenig, falsch oder
        // vertauscht ("RE-266091039" statt "RE-26091039"). Weil die Nummern
        // fortlaufend vergeben werden, liegt die Nachbarnummer immer nur einen
        // Tippfehler entfernt. Vorgeschlagen wird deshalb nur, wenn zusaetzlich
        // der Betrag genau passt; sonst bleibt die Rechnung eine Auswahl.
        const similarToken = new Map<string, string>();
        const similar = openInvoices.filter((invoice) => {
            const core = digitCoreOf(invoice.invoiceNumber ?? '');
            if (!core) return false;
            const token = digitTokens.find((candidate) => withinOneEdit(candidate, core));
            if (token) similarToken.set(invoice.id, token);
            return !!token;
        });
        const typoNote = (invoice: Invoice): string =>
            `Im Verwendungszweck steht „${similarToken.get(invoice.id)}“ — fast die Nummer von ${invoiceLabel(
                invoice,
            )} (ein Zeichen abweichend, vermutlich ein Tippfehler)`;
        const withSimilar = (list: readonly Invoice[], exclude?: Invoice): Invoice[] => {
            const out: Invoice[] = [];
            for (const invoice of [...list, ...similar]) {
                if (invoice.id === exclude?.id) continue;
                if (!out.some((known) => known.id === invoice.id)) out.push(invoice);
            }
            return out;
        };

        const similarByAmount = similar.filter((invoice) =>
            amountsEqual(transaction.amountCents, openOf(invoice), tolerance),
        );

        if (similarByAmount.length === 1) {
            const invoice = similarByAmount[0];
            return {
                transaction,
                invoice,
                candidates: withSimilar([], invoice),
                confidence: 'medium',
                rule: 'similar_number',
                reason: `${typoNote(invoice)}, und der Betrag entspricht genau dem offenen Betrag. Bitte prüfen.`,
            };
        }

        if (similarByAmount.length > 1) {
            const byName = similarByAmount.filter(matchesPayer);
            if (byName.length === 1) {
                return {
                    transaction,
                    invoice: byName[0],
                    candidates: similarByAmount,
                    confidence: 'low',
                    rule: 'similar_number',
                    reason: `${similarByAmount.length} Rechnungen mit fast derselben Nummer haben denselben offenen Betrag; nur bei ${invoiceLabel(
                        byName[0],
                    )} passt zusätzlich der Name des Zahlers. Bitte prüfen.`,
                };
            }
            return {
                transaction,
                invoice: null,
                candidates: similarByAmount,
                confidence: 'low',
                rule: 'ambiguous',
                reason: `${similarByAmount.length} Rechnungen mit fast derselben Nummer (${similarByAmount
                    .map((invoice) => invoice.invoiceNumber)
                    .join(', ')}) haben denselben offenen Betrag. Bitte von Hand zuordnen.`,
            };
        }

        const byAmount = openInvoices.filter((invoice) =>
            amountsEqual(transaction.amountCents, openOf(invoice), tolerance),
        );

        if (byAmount.length === 1) {
            return {
                transaction,
                invoice: byAmount[0],
                candidates: withSimilar([], byAmount[0]),
                confidence: 'medium',
                rule: 'open_amount',
                reason: `Der Betrag entspricht genau dem offenen Betrag von ${invoiceLabel(
                    byAmount[0],
                )}. Eine Rechnungsnummer steht nicht im Verwendungszweck.`,
            };
        }

        if (byAmount.length > 1) {
            const byName = byAmount.filter(matchesPayer);
            if (byName.length === 1) {
                return {
                    transaction,
                    invoice: byName[0],
                    candidates: withSimilar(byAmount),
                    confidence: 'low',
                    rule: 'customer_amount',
                    reason: `${byAmount.length} Rechnungen haben denselben offenen Betrag; nur bei ${invoiceLabel(
                        byName[0],
                    )} passt zusätzlich der Name des Zahlers. Bitte prüfen.`,
                };
            }
            return {
                transaction,
                invoice: null,
                candidates: withSimilar(byAmount),
                confidence: 'low',
                rule: 'ambiguous',
                reason: `${byAmount.length} Rechnungen haben denselben offenen Betrag von ${formatCents(
                    transaction.amountCents,
                )} und der Verwendungszweck nennt keine Rechnungsnummer. Bitte von Hand zuordnen.`,
            };
        }

        // Kein passender Betrag. Wenn der Zahler erkennbar ist, werden seine
        // offenen Rechnungen als Auswahl mitgegeben — Teil- und Sammelzahlungen
        // sind der haeufigste Grund, warum kein Betrag passt.
        const byPayer = openInvoices.filter(matchesPayer);
        if (byPayer.length) {
            return {
                transaction,
                invoice: null,
                candidates: withSimilar(byPayer),
                confidence: 'low',
                rule: 'unmatched',
                reason: `Der Zahler hat ${byPayer.length} offene Rechnung(en), der Betrag passt aber zu keiner davon — möglicherweise eine Teil- oder Sammelzahlung. Bitte von Hand zuordnen.`,
            };
        }

        if (similar.length) {
            const note =
                similar.length === 1
                    ? typoNote(similar[0])
                    : `Im Verwendungszweck steht eine Nummer, die fast zu ${similar.length} offenen Rechnungen passt (${similar
                          .map((invoice) => invoice.invoiceNumber)
                          .join(', ')})`;
            return {
                transaction,
                invoice: null,
                candidates: similar,
                confidence: 'low',
                rule: 'unmatched',
                reason: `${note}; der Betrag passt aber nicht zum offenen Betrag. Bitte von Hand zuordnen.`,
            };
        }

        return {
            ...empty,
            confidence: 'low',
            rule: 'unmatched',
            reason: 'Kein Vorschlag: weder eine Rechnungsnummer im Verwendungszweck noch ein passender offener Betrag.',
        };
    };

    return flagDoubleUse(transactions.map(matchOne));
};

/**
 * Entschaerft den Fall, dass dieselbe Rechnung fuer mehrere Umsaetze
 * vorgeschlagen wird.
 *
 * Ohne diesen Durchgang wuerde die Oberflaeche zwei Vorschlaege mit gleicher
 * Sicherheit anzeigen, von denen einer zwangslaeufig falsch ist — und beide
 * liessen sich nacheinander bestaetigen. Der beste Vorschlag bleibt stehen,
 * die uebrigen behalten die Rechnung als Hinweis, verlieren aber ihre
 * Sicherheit.
 */
const flagDoubleUse = (matches: PaymentMatch[]): PaymentMatch[] => {
    const byInvoice = new Map<string, number[]>();
    matches.forEach((match, index) => {
        if (!match.invoice) return;
        const list = byInvoice.get(match.invoice.id);
        if (list) list.push(index);
        else byInvoice.set(match.invoice.id, [index]);
    });

    const result = [...matches];
    byInvoice.forEach((indexes) => {
        if (indexes.length < 2) return;
        const ranked = [...indexes].sort((a, b) => {
            const diff = CONFIDENCE_RANK[matches[b].confidence] - CONFIDENCE_RANK[matches[a].confidence];
            return diff !== 0 ? diff : a - b;
        });
        const winner = matches[ranked[0]];
        for (const index of ranked.slice(1)) {
            result[index] = {
                ...matches[index],
                confidence: 'low',
                rule: 'duplicate_invoice',
                reason: `Für ${invoiceLabel(winner.invoice as Invoice)} wurde bereits der Umsatz vom ${
                    winner.transaction.valueDate
                } über ${formatCents(
                    winner.transaction.amountCents,
                )} vorgeschlagen. Es kann nur einer der beiden gemeint sein.`,
            };
        }
    });
    return result;
};
